type RegisterAddress = {
  addressLine?: string;
  apartmentNumber?: string;
  postalCode: string;
  city?: string;
  state?: string;
  country?: string;
  county?: string;
  addressType: string;
};

type RegisterContact = {
  firstName?: string;
  lastName?: string;
  email?: string;
  dob?: string | null;
  is_primary?: boolean;
  gender?: string;
  race?: string;
  ethinicity?: string;
  militaryStatus?: string;
  isMigrantFarmWorker?: boolean | null;
  isDisabled?: boolean | null;
  citizenship?: string;
  isReceivingIncome?: boolean | null;
  ssn?: string;
};

export type SeniorCenterRegisterRequest = {
  name: string;
  email: string;
  phone: string;
  isConfirmed: boolean;
  identificationType: string;
  identificationNumber: string;
  addresses: RegisterAddress[];
  contacts: RegisterContact[];
};

const firstNonEmpty = (...values: Array<string | undefined | null>): string => {
  for (const value of values) {
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
};

const parseNullableBool = (value: unknown): boolean | null => {
  if (value == null || value === '') {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'yes' || normalized === 'true') {
    return true;
  }
  if (normalized === 'no' || normalized === 'false') {
    return false;
  }
  return null;
};

const parseDob = (dob: string): string | null => {
  if (!dob?.trim()) {
    return null;
  }
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

const normalizePhoneDigits = (phone: string): string => phone.replace(/\D/g, '');

export const mapSeniorCenterApplyPayloadToRegisterRequest = (
  payload: Record<string, unknown>
): SeniorCenterRegisterRequest => {
  const contactInfo = (payload['contact_information'] as Record<string, unknown>) ?? {};
  const recipients = Array.isArray(payload['recipients']) ? payload['recipients'] : [];
  const recipient = (recipients[0] as Record<string, unknown>) ?? {};

  const email = firstNonEmpty(
    contactInfo['email_address'] as string,
    contactInfo['primary_contact_email'] as string,
    payload['primary_contact_email'] as string,
    recipient['recipient_email'] as string,
    recipient['email'] as string
  );

  // Prefer recipient phone — that is the participant number shown at card check-in.
  const phone = firstNonEmpty(
    recipient['phone'] as string,
    recipient['recipient_phone'] as string,
    contactInfo['phone'] as string
  );

  // Prefer recipient identity — senior center check-in verifies the participant, not the household primary.
  const firstName = firstNonEmpty(
    recipient['first_name'] as string,
    recipient['recipient_first_name'] as string,
    payload['first_name'] as string
  );
  const lastName = firstNonEmpty(
    recipient['last_name'] as string,
    recipient['recipient_last_name'] as string,
    payload['last_name'] as string
  );
  const dob = firstNonEmpty(
    recipient['dob'] as string,
    recipient['date_of_birth'] as string,
    recipient['recipient_dob'] as string,
    recipient['recipient_date_of_birth'] as string,
    payload['dob'] as string
  );
  const householdName = firstNonEmpty(
    payload['household_name'] as string,
    `${firstName} ${lastName}`.trim(),
    recipient['recipient_name'] as string,
    recipient['name'] as string
  );
  const ssn = String(recipient['ssn'] ?? '');

  const addresses: RegisterAddress[] = [];
  const addressArray = payload['address'];
  if (Array.isArray(addressArray)) {
    for (const item of addressArray) {
      const row = item as Record<string, unknown>;
      addresses.push({
        addressLine: String(row['address_line'] ?? ''),
        apartmentNumber: String(row['apartment_number'] ?? ''),
        postalCode: firstNonEmpty(row['postal_code'] as string, '00000'),
        city: String(row['city'] ?? ''),
        state: String(row['state'] ?? ''),
        country: firstNonEmpty(row['country'] as string, 'United States'),
        county: String(row['county'] ?? ''),
        addressType: firstNonEmpty(row['address_type'] as string, 'residential'),
      });
    }
  }

  if (addresses.length === 0) {
    addresses.push({
      addressLine: 'Pending',
      apartmentNumber: '',
      postalCode: '00000',
      city: '',
      state: '',
      country: 'United States',
      county: '',
      addressType: 'residential',
    });
  }

  return {
    name: householdName,
    email,
    phone,
    isConfirmed: true,
    identificationType: 'Social Security Number (SSN)',
    identificationNumber: ssn,
    addresses,
    contacts: [
      {
        firstName,
        lastName,
        email,
        dob: parseDob(dob),
        is_primary: true,
        gender: firstNonEmpty(
          recipient['sex'] as string,
          recipient['gender'] as string,
          payload['gender'] as string
        ),
        race: firstNonEmpty(recipient['race'] as string, payload['race'] as string),
        ethinicity: firstNonEmpty(
          recipient['ethnicity'] as string,
          payload['ethnicity'] as string
        ),
        militaryStatus: String(payload['military_status'] ?? ''),
        isMigrantFarmWorker: parseNullableBool(payload['is_migrant_farm_worker']),
        isDisabled: parseNullableBool(
          firstNonEmpty(recipient['is_disabled'] as string, payload['is_disabled'] as string)
        ),
        citizenship: firstNonEmpty(
          recipient['citizenship'] as string,
          payload['citizenship'] as string
        ),
        isReceivingIncome: parseNullableBool(
          firstNonEmpty(
            recipient['is_receiving_income'] as string,
            payload['is_receiving_income'] as string
          )
        ),
        ssn,
      },
    ],
  };
};

/**
 * Maps a second-senior / spouse recipient into a register/profile payload.
 * Prefers recipient identity so the new users row is searchable by spouse name/phone.
 */
export const mapSecondSeniorRecipientToRegisterRequest = (
  payload: Record<string, unknown>
): SeniorCenterRegisterRequest => {
  const contactInfo = (payload['contact_information'] as Record<string, unknown>) ?? {};
  const recipients = Array.isArray(payload['recipients']) ? payload['recipients'] : [];
  const recipient = (recipients[0] as Record<string, unknown>) ?? {};

  const householdEmail = firstNonEmpty(
    contactInfo['email_address'] as string,
    contactInfo['primary_contact_email'] as string,
    payload['primary_contact_email'] as string
  );
  const householdPhone = firstNonEmpty(
    contactInfo['phone'] as string,
    payload['phone'] as string
  );

  const recipientEmail = firstNonEmpty(
    recipient['recipient_email'] as string,
    recipient['email'] as string
  );
  const recipientPhone = firstNonEmpty(
    recipient['recipient_phone'] as string,
    recipient['phone'] as string
  );

  const uniqueKey =
    globalThis.crypto?.randomUUID?.()?.replace(/-/g, '') ??
    `${Date.now()}${Math.floor(Math.random() * 1_000_000)}`;

  const email =
    recipientEmail &&
    (!householdEmail || recipientEmail.toLowerCase() !== householdEmail.toLowerCase())
      ? recipientEmail
      : `second-senior-${uniqueKey}@pending.local`;

  const phone =
    recipientPhone &&
    (!householdPhone || normalizePhoneDigits(recipientPhone) !== normalizePhoneDigits(householdPhone))
      ? recipientPhone
      : `+1998${uniqueKey.replace(/\D/g, '').slice(0, 10).padEnd(10, '0')}`;

  const firstName = firstNonEmpty(
    recipient['first_name'] as string,
    recipient['recipient_first_name'] as string,
    payload['first_name'] as string
  );
  const lastName = firstNonEmpty(
    recipient['last_name'] as string,
    recipient['recipient_last_name'] as string,
    payload['last_name'] as string
  );
  const name = firstNonEmpty(
    `${firstName} ${lastName}`.trim(),
    recipient['recipient_name'] as string,
    recipient['name'] as string,
    'Second Senior'
  );

  const dob = firstNonEmpty(
    recipient['dob'] as string,
    recipient['date_of_birth'] as string,
    recipient['recipient_dob'] as string,
    payload['dob'] as string
  );
  const ssn = firstNonEmpty(recipient['ssn'] as string, recipient['recipient_ssn'] as string);

  const base = mapSeniorCenterApplyPayloadToRegisterRequest(payload);
  return {
    ...base,
    name,
    email,
    phone,
    identificationType: 'Social Security Number (SSN)',
    identificationNumber: ssn,
    contacts: [
      {
        ...(base.contacts[0] ?? {}),
        firstName,
        lastName,
        email,
        dob: parseDob(dob),
        is_primary: true,
        gender: firstNonEmpty(
          recipient['sex'] as string,
          recipient['gender'] as string,
          base.contacts[0]?.gender
        ),
        race: firstNonEmpty(recipient['race'] as string, base.contacts[0]?.race),
        ethinicity: firstNonEmpty(
          recipient['ethnicity'] as string,
          base.contacts[0]?.ethinicity
        ),
        citizenship: firstNonEmpty(
          recipient['citizenship'] as string,
          base.contacts[0]?.citizenship
        ),
        ssn,
      },
    ],
  };
};

export const isSecondSeniorSpouseRecipient = (payload: Record<string, unknown>): boolean => {
  const recipients = Array.isArray(payload['recipients']) ? payload['recipients'] : [];
  const recipient = (recipients[0] as Record<string, unknown>) ?? {};
  return (
    recipient['recipient_is_second_senior_spouse'] === true ||
    String(recipient['recipient_is_second_senior_spouse'] ?? '').toLowerCase() === 'true'
  );
};

export const isSecondSeniorFlowActive = (): boolean => {
  if (typeof sessionStorage !== 'undefined') {
    try {
      if (sessionStorage.getItem('senior_center_second_senior_flow') === 'true') {
        return true;
      }
    } catch {
      // ignore
    }
  }

  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('secondSenior') === 'true') {
        return true;
      }
    } catch {
      // ignore
    }
  }

  return false;
};

export const enrichSecondSeniorApplyPayload = (
  payload: Record<string, unknown>
): Record<string, unknown> => {
  return {
    ...payload,
    participant_origin: 'second_senior',
  };
};
